/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import { TabbedFullWidthAssetItemFragment, TabbedFullWidthAssetSectionFragment } from "@cosmos/hygraph-schema";

import { ResponsiveContainer, SectionContainer, TabbedButtonList } from "@components/elements";
import { styled } from "@panda/jsx";
import { vstack } from "@panda/patterns";

import { TabbedFullWidthAssetContent } from "./TabbedFullWidthAsset.Content";
import { HeroAlphaFullWidthImage } from "../../heroes/alpha/HeroAlpha.FullWidthImage";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { css } from "@panda/css";

const Styled = styled("main", {
	base: {
		display: "block",
		width: "100%",
	},
});

const tabsStyle = css({
	bottom: "eds.dimension.48",
	position: "absolute",
	marginInlineEnd: "eds.dimension.24",
	zIndex: "1",
	gap: "eds.dimension.12",
	smDown: {
		bottom: "eds.dimension.64",
	},
	xsDown: {
		bottom: "eds.dimension.80",
	},
});

const analyticsEventValue = {
	component: "TabbedFullWidthAsset",
	section: "section",
};

export const TabbedFullWidthAssetSection = (
	props: TabbedFullWidthAssetSectionFragment | null,
): React.JSX.Element | null => {
	const tabs = useMemo(() => props?.tabs || [], [props?.tabs]);
	const active = tabs[0] || {};
	const [activeTab, setActiveTab] = useState<TabbedFullWidthAssetItemFragment>(active);

	useEffect(() => {
		if (tabs.length > 0 && !activeTab) setActiveTab(tabs[0]);
	}, [tabs, activeTab]);

	const tabsRef = useRef(tabs);
	useEffect(() => {
		tabsRef.current = tabs;
	}, [tabs]);

	const handleButtonClick = useCallback((index: number) => {
		const tab = Number.isInteger(index) ? tabsRef.current.at(index) : undefined;
		if (tab) setActiveTab(tab);
	}, []);

	const validContent = useMemo(
		() =>
			Boolean(
				activeTab?.heroAsset ||
				activeTab?.eyebrow ||
				activeTab?.title?.text ||
				activeTab?.desc ||
				activeTab?.buttons?.length,
			),
		[tabs?.length, activeTab],
	);

	if (!tabs?.length) return null;

	const enableTabs = tabs.length > 1;

	return (
		<Styled className={`tabbed-full-width-asset-section ${vstack({ gap: "12" })}`}>
			<HeroAlphaFullWidthImage heroAsset={activeTab?.heroAsset} />
			<ResponsiveContainer>
				<SectionContainer overflowX="clip" contentVisibility="unset" h="100vh" minHeight="540px">
					{validContent ? (
						<TabbedFullWidthAssetContent
							heroAsset={activeTab?.heroAsset}
							eyebrow={activeTab?.eyebrow}
							title={activeTab?.title}
							desc={activeTab?.desc}
							buttons={activeTab?.buttons || []}
							gradient={activeTab?.gradient}
						/>
					) : null}
					{enableTabs ? (
						<TabbedButtonList
							onTabChange={handleButtonClick}
							tabs={tabs}
							analyticsEventValue={analyticsEventValue}
							className={tabsStyle}
							buttonSize="lg"
						/>
					) : null}
				</SectionContainer>
			</ResponsiveContainer>
		</Styled>
	);
};
